import React, { useCallback } from "react";
import HeaderPanel from "../layout/HeaderPanel";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { duplicateDocumentRootAsync, normalizePath } from "../../redux/feature/document/document-slice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import {
  selectBackendUrl,
  selectClientId,
  selectCpmUsername,
  selectTenantId,
} from "../../redux/feature/settings/configurations-slice";
import { elaborationStatusItemIsBlocking } from "../../redux/feature/object/remote-elaborations-slice";
import { DocumentData, EditDocument } from "./EditDocument";
import { fetchDocumentMetadata, fetchDocumentPartDetails } from "../../api/document/DocumentsPart";
import { NumberFormat } from "../../api/models/number-format";
import { getDefaultBuiltInFormattingString } from "../../utils/helpers/ObjectFormattingHelper";
import { FormatTypeEnum } from "../../api/models/format-type-enum";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../utils/constants/eventChannel";
import { DUPLICATE_ELABORATION } from "../../utils/constants/notification";
import { useFilteredElaborations } from "../generic/GenericElaborationSelector";
import { ElaborationWrapper } from "../coobjects/ElaborationWrapper";

interface DuplicateProps {
  documentId: string;
  documentName: string;
  onCloseModal: () => void;
  channelIdToNotify: string;
}

export const DuplicateDocumentPanel: React.FunctionComponent<DuplicateProps> = (props) => {
  const username = useAppSelector(selectCpmUsername); // TEMP waiting for real login
  const backendUrl = useAppSelector(selectBackendUrl);
  const tenantId = useAppSelector(selectTenantId);
  const clientId = useAppSelector(selectClientId);
  const dispatch = useAppDispatch();

  const duplicateDocument = async (data: DocumentData) => {
    await duplicateDocumentRootAsync({
      documentId: props.documentId,
      channelIdToNotify: props.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      sharepointPath: normalizePath(data.path),
      username,
      tenantId,
      dispatch,
      backendUrl,
      documentName: data.name,
      documentPartMetadata: data.parameters,
      advanced: data.advanced,
      language: data.language,
      clientId,
      duplicateAttachments: data.duplicateAttachments,
    });
    props.onCloseModal();
  };

  const addedElaborationsDetails = useAppSelector((store) => store.remoteElaborations.addedElaborationItemsList);
  const blockingElabFindFunction = useCallback(
    (item) => item.elaborationType === "DUPLICATE" && elaborationStatusItemIsBlocking(item, addedElaborationsDetails),
    [addedElaborationsDetails]
  );
  const blockingElaboration = useFilteredElaborations({
    findFunction: blockingElabFindFunction,
  });
  return (
    <ElaborationWrapper notificationKey={DUPLICATE_ELABORATION} elaborationItem={blockingElaboration}>
      <>
        <HeaderPanel data-testid={"header"} applyPadding>
          {formatMessage("cof.documentmanagement.outline.duplicate.tile.rollforwarddocument")}{" "}
          {props.documentName ? " - " + props.documentName : null}
        </HeaderPanel>
        <EditDocument
          onLastBackButton={() => props.onCloseModal()}
          isEdit={false}
          editDocumentOperationType={"DUPLICATE"}
          onSave={(data) => duplicateDocument(data)}
          getDocumentPartDetails={async () => {
            const documentMetadata = await fetchDocumentMetadata(props.documentId);
            const documentPart = await fetchDocumentPartDetails(props.documentId);

            const advanced =
              documentPart.root || documentPart.multiLinked
                ? {
                    numberFormat: documentPart?.metadata?.formatMetadata?.numberFormat?.format,
                    typeNumber: documentPart?.metadata?.formatMetadata?.numberType,
                    percentageFormat: documentPart?.metadata?.formatMetadata?.percentageFormat?.format,
                    typePercentage: documentPart?.metadata?.formatMetadata?.percentageType,
                    dateFormat: (documentPart?.metadata?.formatMetadata?.dateFormat as NumberFormat)?.format,
                    typeDate: documentPart?.metadata?.formatMetadata?.dateType,
                    imageExtension: documentMetadata.formatImage?.imageExtension,
                    imageResolution: documentMetadata.formatImage?.imageResolution,
                    templateInfo: documentMetadata.templateInfo,
                    locale: documentMetadata.formatMetadata?.locale,
                    id: documentPart?.metadata?.id,
                  }
                : {
                    numberFormat: getDefaultBuiltInFormattingString(FormatTypeEnum.NUMBER),
                    typeNumber: "Default",
                    percentageFormat: getDefaultBuiltInFormattingString(FormatTypeEnum.PERCENTAGE),
                    typePercentage: "Default",
                    dateFormat: getDefaultBuiltInFormattingString(FormatTypeEnum.DATE),
                    typeDate: "Default",
                    imageExtension: "png",
                    imageResolution: 150,
                    templateInfo: documentMetadata.templateInfo,
                    locale: documentMetadata.formatMetadata?.locale,
                    id: null,
                  };
            return {
              sourceDocumentName: documentPart.name,
              documentData: {
                description: documentPart.description,
                name: "",
                root: true,
                multilinked: false,
                path: "",
                documentId: documentPart.id,
                advanced,
                language: documentMetadata.language,
                parameters:
                  documentMetadata.parametersMetadata?.map((p) => ({
                    ...p,
                    dimensions: p.dimensions.map((d) => ({ ...d, inherited: false })),
                  })) ?? [],
              },
              fatherDocumentParameters: undefined,
            };
          }}
        />
      </>
    </ElaborationWrapper>
  );
};
